/**
 * Purpose: This middleware builds and sets the latest offer payload object in memory. Add redux actions to the validActions array when those actions should update the payload.
 */
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { initActionTypes, type ReduxNext, bridgeUserActionTypes } from '@makemydeal/dr-shared-store';

import { SCENARIOS_INGEST_COMPLETE } from '../actionTypes/multiScenarioActionTypes';
import { setLastOfferPayloadObject } from '../utils/unsavedChangesUtils';
import { OFFER_SAVE_SUCCESS } from '../actionTypes/offerActionTypes';
import { UNSAVED_CHANGES_REBASELINED } from '../actionTypes/unsavedChangesActionTypes';
import { FETCH_RATES_FAILURE, FETCH_RATES_SUCCESS } from '../actionTypes/vehicleProtectionActionTypes';
import { offerReduxActions } from '../actions/offerRedux';
import * as unsavedChangesSelectors from '../selectors/unsavedChangesSelectors';

const validActions: string[] = [
    bridgeUserActionTypes.BRIDGE_USER_SUCCESS,
    SCENARIOS_INGEST_COMPLETE,
    initActionTypes.BOOTSTRAP_SUCCESS,
    OFFER_SAVE_SUCCESS,
    FETCH_RATES_SUCCESS,
    FETCH_RATES_FAILURE
];

const validInitActions: string[] = [offerReduxActions.INIT_SUCCESS, offerReduxActions.MAKE_CURRENT_OFFER];

const isRebaselineWhenCleanRequested = (action: AnyFSA): boolean =>
    action.meta?.rebaselineUnsavedChangesWhenClean === true ||
    action.meta?.originalAction?.meta?.rebaselineUnsavedChangesWhenClean === true;

export const middleware = (store: any) => (next: ReduxNext) => (action: AnyFSA) => {
    const rebaselineWhenClean =
        isRebaselineWhenCleanRequested(action) && !unsavedChangesSelectors.currentOfferHasUnsavedChanges(store.getState());

    next(action);

    const state = store.getState();

    // Per-action opt-in for load-time seeds, which land after the baseline and would otherwise read as user
    // edits. Bypasses the allowlist below — allowlisting the seed's type would cover real edits of the field too.
    if (action.meta?.rebaselineUnsavedChanges === true) {
        setLastOfferPayloadObject(state);
        return;
    }

    if (rebaselineWhenClean) {
        Promise.resolve()
            .then(() => {
                setLastOfferPayloadObject(store.getState());
                store.dispatch({ type: UNSAVED_CHANGES_REBASELINED });
            })
            .catch((error) => {
                logNewRelicPageAction('MV:UnsavedChanges - Rebaseline Failed', { actionType: action.type, message: String(error) });
            });
        return;
    }

    // `updatePrevOffer: false` stays a narrow opt-out for allowlisted action types.
    if (!validActions.includes(action.type) || action.meta?.updatePrevOffer === false) {
        return;
    }

    switch (action.type) {
        case FETCH_RATES_FAILURE:
        case FETCH_RATES_SUCCESS: {
            if (validInitActions.includes(action.meta?.originalAction?.meta?.originalAction?.type)) {
                setLastOfferPayloadObject(state);
            }
            break;
        }
        default:
            setLastOfferPayloadObject(state);
    }
    return;
};
